import { useEffect, useState } from "react";
import { Link2, Loader2, AlertTriangle, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Teller Connect button: opens the Teller widget directly on click, exchanges
 * the resulting enrollment with the backend, then triggers an initial sync.
 *
 * Previously this button opened an intermediate "confirm workspace" dialog
 * before launching Teller. Several users reported "the button does nothing" —
 * they were seeing our confirm dialog, not realising they had to click again
 * to actually open Teller. The dialog has been replaced with a persistent
 * workspace label right next to the button so the safety info is still shown
 * but the click→Teller flow is instant.
 *
 * Props:
 *   onConnected: callback invoked after a successful connection + initial sync
 */
export default function TellerConnectButton({ onConnected }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const { tenant } = useAuth();

  useEffect(() => {
    let cancelled = false;
    api.get("/bank-connect/config")
      .then((r) => { if (!cancelled) setConfig(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const openConnect = () => {
    if (!config?.configured) {
      toast.error(config?.message || "Bank-connect is not configured.");
      return;
    }
    if (typeof window.TellerConnect === "undefined") {
      toast.error("Teller Connect failed to load. Refresh the page and try again.");
      return;
    }
    setBusy(true);
    const teller = window.TellerConnect.setup({
      applicationId: config.application_id,
      environment: config.environment || "sandbox",
      products: ["transactions", "balance"],
      onSuccess: async (enrollment) => {
        try {
          const enrollmentId = enrollment?.enrollment?.id || enrollment?.id;
          const institutionName = enrollment?.enrollment?.institution?.name
            || enrollment?.institution?.name
            || "Linked bank";
          const accessToken = enrollment?.accessToken || enrollment?.access_token;
          if (!accessToken) {
            toast.error("Teller returned no access token.");
            return;
          }
          // Backend fetches /accounts via MTLS and creates one
          // BankAccount + BankConnection per linked account.
          const { data } = await api.post("/bank-connect/exchange", {
            access_token: accessToken,
            enrollment_id: enrollmentId,
            institution_name: institutionName,
          });
          const connections = data?.connections || [];
          if (!connections.length) {
            toast.error("Teller returned no accounts for this enrollment.");
            return;
          }
          toast.success(
            `Connected ${institutionName} · ${connections.length} account${connections.length > 1 ? "s" : ""} added to ${tenant?.name || "this workspace"}`,
          );
          // Queue background syncs and poll for progress
          for (const conn of connections) {
            try {
              await api.post(`/bank-connections/${conn.id}/sync`);
            } catch {
              continue;
            }
            // Poll with a single sonner toast per connection that updates in-place
            const toastId = toast.loading(`Importing transactions for ${institutionName}…`);
            await new Promise((resolve) => {
              const timer = setInterval(async () => {
                try {
                  const { data } = await api.get(`/bank-connections/${conn.id}/sync-status`);
                  const p = data?.progress || {};
                  if (p.state === "importing" && p.total) {
                    toast.loading(`${institutionName} · ${p.imported || 0}/${p.total} imported`, { id: toastId });
                  } else if (p.state === "fetching" || p.state === "queued") {
                    toast.loading(p.message || "Working…", { id: toastId });
                  } else if (p.state === "done") {
                    toast.success(
                      `${institutionName} · imported ${p.imported || 0} transaction${p.imported === 1 ? "" : "s"}`,
                      { id: toastId },
                    );
                    clearInterval(timer);
                    resolve();
                  } else if (p.state === "error") {
                    toast.error(`${institutionName}: ${p.message || "Sync failed"}`, { id: toastId });
                    clearInterval(timer);
                    resolve();
                  }
                } catch {
                  // Network blip — keep polling
                }
              }, 1200);
              // Hard ceiling 90s so we never hang the UI
              setTimeout(() => { clearInterval(timer); resolve(); }, 90000);
            });
          }
          onConnected?.();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Failed to save Teller enrollment.");
        } finally {
          setBusy(false);
        }
      },
      onExit: () => {
        setBusy(false);
        // If the user closed Teller without completing enrollment, give a
        // targeted hint depending on the environment.
        const env = (config?.environment || "sandbox");
        if (env === "sandbox") {
          toast.message(
            "Teller sandbox tip",
            {
              description: "If the login screen spun, use username: username and password: password — real credentials don't work in sandbox.",
              duration: 6000,
            },
          );
        } else {
          // Development / production — the most common silent failure is an
          // un-whitelisted origin or a bank requiring MFA Teller dev can't
          // service. Point the user to the diagnostic banner.
          toast.message(
            "Didn't finish connecting?",
            {
              description: `If the bank login didn't open, the current URL (${window.location.origin}) might not be whitelisted in your Teller dashboard. See the blue banner on this page for one-click fix.`,
              duration: 7000,
            },
          );
        }
      },
      onFailure: (failure) => {
        // Surface Teller's own error code so users can share it with support.
        const parts = [failure?.message, failure?.code, failure?.type].filter(Boolean);
        toast.error(parts.length ? `Teller: ${parts.join(" · ")}` : "Teller Connect failed.");
        setBusy(false);
      },
    });
    teller.open();
  };

  const configured = !!config?.configured;
  const isSandbox = (config?.environment || "sandbox") === "sandbox";

  return (
    <div className="flex flex-col items-end gap-1" data-testid="teller-connect-wrapper">
      <button
        onClick={openConnect}
        disabled={busy || !config}
        data-testid="teller-connect-btn"
        title={configured ? `Link a bank via Teller — accounts will be added to ${tenant?.name || "this workspace"}` : (config?.message || "Bank-connect not configured")}
        className="text-sm px-4 py-2 border bg-white hover:bg-[#ECFDF5] transition-colors flex items-center gap-1.5 disabled:opacity-60"
        style={{ borderColor: "#10B981", color: "#065F46" }}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
        {busy ? "Connecting…" : "Connect a bank"}
      </button>
      {configured && tenant?.name && (
        <div
          className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] flex items-center gap-1"
          data-testid="teller-connect-target-workspace"
        >
          <Building2 size={10} />
          Adds accounts to <strong className="text-[var(--ll-ink)]">{tenant.name}</strong>
        </div>
      )}
      {configured && isSandbox && (
        <div
          className="text-[10px] uppercase tracking-widest text-[#B45309] flex items-center gap-1"
          data-testid="teller-sandbox-warning"
          title="Teller is running in sandbox mode — only Teller's test banks (Chase, Capital One Sandbox etc.) will appear. Set TELLER_ENVIRONMENT=development or production in backend/.env to connect real banks."
        >
          <AlertTriangle size={10} />
          Sandbox mode · test banks only
        </div>
      )}
    </div>
  );
}
